import React, { useRef, useState } from 'react';
import SlidePanel from '../shared/SlidePanel';
import ProductEditorPanel from './ProductEditorPanel';
import CopyLinkButton from '../shared/shareLinks';
import { sanitizeRichText } from './richText';
import '../shared/ui.css';
import '../new-service/ServiceDetailPanel.css';
import './ProductDetailPanel.css';

/**
 * ProductDetailPanel — read-only product summary in a SlidePanel.
 * Opens when tapping a product in the list. Shows key info at a
 * glance with edit/delete actions. Edit swaps the panel content
 * to an inline ProductEditorPanel (matches ServiceDetailPanel).
 */
const ProductDetailPanel = ({ product, onClose, onDelete, onSaved, currency = '£' }) => {
    const slidePanelRef = useRef(null);
    const [editing, setEditing] = useState(false);

    const name = product?.productName || 'Product';
    const categoryName = product?.category?.categoryName || '';
    const brandName = product?.brand?.brandName || '';
    const description = product?.description || '';
    const retailPrice = Number(product?.retailPrice || 0);
    const costPrice = Number(product?.stockPrice || 0);
    const quantity = product?.quantity || 0;
    const barcode = product?.barcode || '';
    const sku = product?.sku || '';
    const trackStock = product?.trackStock;
    const sellOnline = product?.sellOnline;
    const images = product?.images || [];

    const handleEdit = () => {
        setEditing(true);
    };

    const handleBackFromEdit = () => {
        setEditing(false);
    };

    const handleDelete = () => {
        if (onDelete) onDelete(product?.id, name);
    };

    return (
        <SlidePanel
            ref={slidePanelRef}
            onClose={onClose}
            className={`sdp-panel ${editing ? 'sdp-panel--editing' : ''}`}
            width={editing ? 700 : 420}
        >
            {editing ? (
                <ProductEditorPanel
                    product={product}
                    currency={currency}
                    inline
                    onBack={handleBackFromEdit}
                    onClose={onClose}
                    onSaved={() => {
                        if (onSaved) onSaved();
                        if (window.innerWidth < 768) {
                            onClose();
                        } else {
                            handleBackFromEdit();
                        }
                    }}
                />
            ) : (
            <>
            {/* Header */}
            <div className="sdp-header">
                <div className="sdp-header-info">
                    <h2 className="sdp-header-name">{name}</h2>
                    {(categoryName || brandName) && (
                        <span className="sdp-header-category">
                            {[categoryName, brandName].filter(Boolean).join(' / ')}
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* Only sell-online products have a public shop page to link to */}
                    {sellOnline === 1 && (
                        <CopyLinkButton type="product" id={product?.id} title="Copy product link" />
                    )}
                    <button className="beu-icon-btn" onClick={onClose}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Scrollable body */}
            <div className="sdp-body">
                {/* Product image - large hero */}
                {images.length > 0 && (
                    <div className="pdp-image-hero">
                        <img src={images[0]?.url || images[0]} alt={name} />
                    </div>
                )}

                {/* Price + Quantity summary box. Quantity is hidden when
                    Track Stock is off — matches Shopify / Square / Lightspeed
                    so the merchant doesn't see a count for an item they've
                    explicitly opted out of tracking. */}
                <div className="sdp-stats-row">
                    <div className="sdp-stat">
                        <span className="sdp-stat-value">{currency}{retailPrice.toFixed(2)}</span>
                        <span className="sdp-stat-label">Retail Price</span>
                    </div>
                    <div className="sdp-stat-divider" />
                    <div className="sdp-stat">
                        <span className="sdp-stat-value">{currency}{costPrice.toFixed(2)}</span>
                        <span className="sdp-stat-label">Cost Price</span>
                    </div>
                    {trackStock === 1 && (
                        <>
                            <div className="sdp-stat-divider" />
                            <div className="sdp-stat">
                                <span className="sdp-stat-value">{Math.max(0, parseInt(quantity, 10) || 0)}</span>
                                <span className="sdp-stat-label">Quantity</span>
                            </div>
                        </>
                    )}
                </div>

                {/* Description */}
                {description && (
                    <div className="sdp-section">
                        <p className="beu-section-label">Description</p>
                        <div className="sdp-description" dangerouslySetInnerHTML={{ __html: sanitizeRichText(description) }} />
                    </div>
                )}

                {/* Stock info */}
                <div className="sdp-section">
                    <p className="beu-section-label">Stock</p>
                    <div className="sdp-detail-grid">
                        {barcode && (
                            <div className="sdp-detail-row">
                                <span className="sdp-detail-label">Barcode</span>
                                <span className="sdp-detail-value">{barcode}</span>
                            </div>
                        )}
                        {sku && (
                            <div className="sdp-detail-row">
                                <span className="sdp-detail-label">SKU</span>
                                <span className="sdp-detail-value">{sku}</span>
                            </div>
                        )}
                        <div className="sdp-detail-row">
                            <span className="sdp-detail-label">Track Stock</span>
                            <span className="sdp-detail-value">{trackStock === 1 ? 'Yes' : 'No'}</span>
                        </div>
                        {/* Sell Online — hidden until the booking-page sell-online flow ships. */}
                        {false && (
                            <div className="sdp-detail-row">
                                <span className="sdp-detail-label">Sell Online</span>
                                <span className="sdp-detail-value">{sellOnline === 1 ? 'Yes' : 'No'}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="sdp-footer">
                <button type="button" className="sdp-btn sdp-btn--secondary" onClick={handleDelete}>
                    Delete
                </button>
                <button type="button" className="sdp-btn sdp-btn--primary" onClick={handleEdit}>
                    Edit Product
                </button>
            </div>
            </>
            )}
        </SlidePanel>
    );
};

export default ProductDetailPanel;
